import { Component } from 'react'

async function report(message, stack, context) {
  try {
    const token = localStorage.getItem('adops_token')
    if (!token) return
    await fetch('/api/report-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message, stack, context, url: window.location.href })
    })
  } catch {}
}

// Wrap window.fetch so EVERY failed request is reported —
// including the many components that call fetch() directly instead of api.js
function hookFetch() {
  const orig = window.fetch
  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : (input?.url || '')
    const method = (init?.method || 'GET').toUpperCase()
    const isOurApi = url.startsWith('/api/') || url.includes('/api/')
    const isReporter = url.includes('/api/report-error')
    // Background pollers (dashboard badges) fail with status 0 when a tab is left open
    // and the network sleeps — harmless and unactionable, so don't report those.
    const isSilentPoll = url.includes('/dashboard')
    let res
    try {
      res = await orig.apply(this, arguments)
    } catch (netErr) {
      if (isOurApi && !isReporter && !isSilentPoll) report(`API ${method} ${url} network error: ${netErr.message}`, '', { type:'fetch', url, method, status: 0 })
      throw netErr
    }
    // Report failures on our own API (skip auth events + the reporter itself)
    if (isOurApi && !isReporter && !res.ok && res.status !== 401 && res.status !== 403) {
      try {
        const clone = res.clone()
        const text = await clone.text()
        let msg = `HTTP ${res.status}`
        try { const j = JSON.parse(text); msg = j.error || j.message || msg } catch { msg = text.slice(0, 200) || msg }
        report(`API ${method} ${url} failed: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`, '', { type:'fetch', url, method, status: res.status })
      } catch {}
    }
    return res
  }
}

// Catch unhandled JS errors + promise rejections app-wide
let globalHooked = false
export function hookGlobalErrors() {
  if (globalHooked) return
  globalHooked = true
  hookFetch()
  window.addEventListener('error', (e) => {
    report(e.message || 'Window error', e.error?.stack || '', { type: 'window.onerror', file: e.filename, line: e.lineno })
  })
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason
    report('Unhandled rejection: ' + (r?.message || String(r)), r?.stack || '', { type: 'unhandledrejection' })
  })
}

export class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, message: '' } }
  static getDerivedStateFromError(error) { return { hasError: true, message: error?.message || 'Unknown error' } }
  componentDidCatch(error, info) {
    report(error?.message || 'React crash', error?.stack || '', {
      type: 'react-boundary',
      component: info?.componentStack?.split('\n')[1]?.trim() || '',
      section: this.props.section || '',
    })
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding:40,textAlign:'center',color:'var(--text2)'}}>
          <div style={{fontSize:34,marginBottom:12}}>⚠️</div>
          <div style={{fontSize:15,fontWeight:700,color:'var(--text)'}}>Something went wrong</div>
          <div style={{fontSize:12,marginTop:6,color:'var(--text3)'}}>The team has been notified automatically.</div>
          <button className="btn btn-primary" style={{marginTop:16}} onClick={() => window.location.reload()}>Reload page</button>
        </div>
      )
    }
    return this.props.children
  }
}
